import { Launch } from '@mui/icons-material'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { chapterSectionKey } from '@shared/chapterSectionsOrder'
import {
  LOVE_ROOT_TAG_ID,
  RATING_SCALE,
  READINESS_ROOT_TAG_ID,
  type RatingDimension,
  type SectionRatings,
  splitSectionTags,
} from '@shared/tags/canonicalRatingTags'
import type { TagDefinition } from '@shared/tags/tagRegistry'
import { useCallback, useMemo, useState } from 'react'

import type { ChapterSectionEntry } from '../hooks/useChapterSectionsBook'

// ---------------------------------------------------------------------------
// Types + helpers
// ---------------------------------------------------------------------------

export type RatingFilter = { love: Set<number>; readiness: Set<number>; groups: Set<string> }

export const EMPTY_RATING_FILTER: RatingFilter = { love: new Set(), readiness: new Set(), groups: new Set() }

export function ratingFilterActive(filter: RatingFilter): boolean {
  return filter.love.size > 0 || filter.readiness.size > 0 || filter.groups.size > 0
}

export function rowMatchesRatingFilter(
  ratings: SectionRatings,
  primaryGroup: string | null,
  filter: RatingFilter,
): boolean {
  if (filter.love.size > 0) {
    if (ratings.love === undefined || !filter.love.has(ratings.love)) {
      return false
    }
  }
  if (filter.readiness.size > 0) {
    if (ratings.readiness === undefined || !filter.readiness.has(ratings.readiness)) {
      return false
    }
  }
  if (filter.groups.size > 0) {
    if (!primaryGroup || !filter.groups.has(primaryGroup)) {
      return false
    }
  }
  return true
}

export function sectionPrimaryGroup(row: { ancestorLabels: string[]; chapter: { title: string } }): string | null {
  const label = (row.ancestorLabels[0] || row.chapter.title || '').trim()
  return label || null
}

// ---------------------------------------------------------------------------
// Hook: manages filter state, computes stats and row matcher
// ---------------------------------------------------------------------------

export interface SectionRatingFilterApi {
  filter: RatingFilter
  isActive: boolean
  count: number
  stats: {
    love: Record<number, number>
    readiness: Record<number, number>
    groups: Map<string, number>
    matching: number
    total: number
  }
  toggleLevel: (dimension: RatingDimension, n: number) => void
  toggleGroup: (label: string) => void
  clear: () => void
  matches: (entry: ChapterSectionEntry) => boolean
  firstEntryByGroup: Map<string, ChapterSectionEntry>
}

export function useSectionRatingFilter({
  entries,
  tagRegistry,
  effectiveTagIds,
}: {
  entries: ChapterSectionEntry[]
  tagRegistry: Record<string, TagDefinition> | undefined
  effectiveTagIds: (key: string) => string[]
}): SectionRatingFilterApi {
  const [filter, setFilter] = useState<RatingFilter>(EMPTY_RATING_FILTER)

  const isActive = useMemo(() => ratingFilterActive(filter), [filter])
  const count = filter.love.size + filter.readiness.size + filter.groups.size

  const stats = useMemo(() => {
    const love: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    const readiness: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    const groups = new Map<string, number>()
    let matching = 0
    if (!tagRegistry) {
      return { love, readiness, groups, matching: entries.length, total: entries.length }
    }
    for (const entry of entries) {
      const key = chapterSectionKey(entry.chapter.filename, entry.headingBlockId)
      const { ratings } = splitSectionTags(effectiveTagIds(key), tagRegistry)
      if (ratings.love !== undefined) {
        love[ratings.love] = (love[ratings.love] ?? 0) + 1
      }
      if (ratings.readiness !== undefined) {
        readiness[ratings.readiness] = (readiness[ratings.readiness] ?? 0) + 1
      }
      const primary = sectionPrimaryGroup(entry)
      if (primary) {
        groups.set(primary, (groups.get(primary) ?? 0) + 1)
      }
      if (rowMatchesRatingFilter(ratings, primary, filter)) {
        matching++
      }
    }
    return { love, readiness, groups, matching, total: entries.length }
  }, [entries, tagRegistry, filter, effectiveTagIds])

  const toggleLevel = useCallback((dimension: RatingDimension, n: number) => {
    setFilter((prev) => {
      const nextSet = new Set(prev[dimension])
      if (nextSet.has(n)) {
        nextSet.delete(n)
      } else {
        nextSet.add(n)
      }
      return { ...prev, [dimension]: nextSet }
    })
  }, [])

  const toggleGroup = useCallback((label: string) => {
    setFilter((prev) => {
      const nextSet = new Set(prev.groups)
      if (nextSet.has(label)) {
        nextSet.delete(label)
      } else {
        nextSet.add(label)
      }
      return { ...prev, groups: nextSet }
    })
  }, [])

  const clear = useCallback(() => {
    setFilter(EMPTY_RATING_FILTER)
  }, [])

  const matches = useCallback(
    (entry: ChapterSectionEntry) => {
      if (!tagRegistry) {
        return true
      }
      const key = chapterSectionKey(entry.chapter.filename, entry.headingBlockId)
      const { ratings } = splitSectionTags(effectiveTagIds(key), tagRegistry)
      return rowMatchesRatingFilter(ratings, sectionPrimaryGroup(entry), filter)
    },
    [filter, tagRegistry, effectiveTagIds],
  )

  const firstEntryByGroup = useMemo(() => {
    const map = new Map<string, ChapterSectionEntry>()
    for (const entry of entries) {
      const key = sectionPrimaryGroup(entry)
      if (key && !map.has(key)) {
        map.set(key, entry)
      }
    }
    return map
  }, [entries])

  return { filter, isActive, count, stats, toggleLevel, toggleGroup, clear, matches, firstEntryByGroup }
}

// ---------------------------------------------------------------------------
// FilterBarChart — horizontal row of rating bars
// ---------------------------------------------------------------------------

export function FilterBarChart({
  dimension,
  selected,
  onToggle,
  counts,
  tagRegistry,
  label,
}: {
  dimension: RatingDimension
  selected: Set<number>
  onToggle: (n: number) => void
  counts: Record<number, number>
  tagRegistry: Record<string, TagDefinition>
  label: string
}) {
  const rootId = dimension === 'love' ? LOVE_ROOT_TAG_ID : READINESS_ROOT_TAG_ID
  const color = tagRegistry[rootId]?.color ?? '#9e9e9e'
  const pastel = `color-mix(in srgb, ${color} 45%, white)`
  const ringMuted = `color-mix(in srgb, ${color} 35%, transparent)`
  const softFill = `color-mix(in srgb, ${color} 22%, transparent)`
  const MAX_BAR_HEIGHT = 26
  const max = Math.max(1, ...Array.from({ length: RATING_SCALE }, (_, i) => counts[i + 1] ?? 0))

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', minWidth: 74, pb: 2 }}>{label}</Typography>
      <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: 0.5 }}>
        {Array.from({ length: RATING_SCALE }, (_, i) => {
          const n = i + 1
          const count = counts[n] ?? 0
          const active = selected.has(n)
          const barHeight = count === 0 ? 2 : Math.max(4, (count / max) * MAX_BAR_HEIGHT)
          return (
            <Box
              key={n}
              role="checkbox"
              aria-checked={active}
              aria-label={`${label} ${n}: ${count} sections`}
              onClick={() => onToggle(n)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
                cursor: 'pointer',
                width: 16,
                userSelect: 'none',
              }}
            >
              <Typography
                sx={{
                  fontSize: '8px',
                  fontWeight: 600,
                  lineHeight: 1,
                  color: count > 0 ? 'text.secondary' : 'text.disabled',
                }}
              >
                {count}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: barHeight,
                  borderRadius: 0.5,
                  backgroundColor: active ? pastel : count > 0 ? softFill : 'transparent',
                  border: `1px solid ${active ? pastel : ringMuted}`,
                  transition: 'all 0.12s',
                  '&:hover': { backgroundColor: active ? pastel : `color-mix(in srgb, ${color} 15%, transparent)` },
                }}
              />
              <Typography
                sx={{
                  fontSize: '9px',
                  fontWeight: 700,
                  lineHeight: 1,
                  color: active ? `color-mix(in srgb, ${color} 75%, black)` : 'text.secondary',
                }}
              >
                {n}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// GroupFilterList — per-ancestor group counts, with optional jump affordance
// ---------------------------------------------------------------------------

export function GroupFilterList({
  counts,
  selected,
  onToggle,
  onJump,
}: {
  counts: Map<string, number>
  selected: Set<string>
  onToggle: (group: string) => void
  onJump?: (group: string) => void
}) {
  const sorted = useMemo(
    () => Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'he')),
    [counts],
  )
  if (sorted.length === 0) {
    return null
  }
  const max = Math.max(1, ...sorted.map(([, n]) => n))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.5 }}>קבוצות</Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          maxHeight: 180,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'var(--border-subtle)', borderRadius: 2 },
        }}
      >
        {sorted.map(([label, count]) => {
          const active = selected.has(label)
          return (
            <Box
              key={label}
              role="checkbox"
              aria-checked={active}
              onClick={() => onToggle(label)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                cursor: 'pointer',
                bgcolor: active ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '11px',
                  fontWeight: active ? 600 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
              <Box
                sx={{
                  width: 44,
                  height: 4,
                  borderRadius: 2,
                  flexShrink: 0,
                  bgcolor: 'var(--border-subtle)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    insetInlineStart: 0,
                    top: 0,
                    bottom: 0,
                    width: `${(count / max) * 100}%`,
                    bgcolor: active ? 'primary.main' : 'text.secondary',
                    opacity: active ? 0.9 : 0.5,
                  }}
                />
              </Box>
              <Typography
                sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary', minWidth: 18, textAlign: 'end' }}
              >
                {count}
              </Typography>
              {onJump && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    onJump(label)
                  }}
                  title="קפוץ לכותרת"
                  sx={{ p: 0.25, flexShrink: 0, color: 'text.secondary' }}
                >
                  <Launch sx={{ fontSize: 12 }} />
                </IconButton>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// SectionRatingFilterPanel — full popover body (bars + groups + counts + clear)
// ---------------------------------------------------------------------------

export function SectionRatingFilterPanel({
  api,
  tagRegistry,
  onJumpToGroup,
}: {
  api: SectionRatingFilterApi
  tagRegistry: Record<string, TagDefinition>
  onJumpToGroup?: (group: string) => void
}) {
  return (
    <>
      <FilterBarChart
        dimension="love"
        selected={api.filter.love}
        onToggle={(n) => api.toggleLevel('love', n)}
        counts={api.stats.love}
        tagRegistry={tagRegistry}
        label="שביעות רצון"
      />
      <FilterBarChart
        dimension="readiness"
        selected={api.filter.readiness}
        onToggle={(n) => api.toggleLevel('readiness', n)}
        counts={api.stats.readiness}
        tagRegistry={tagRegistry}
        label="רמת מוכנות"
      />
      {api.stats.groups.size > 0 && (
        <Box sx={{ borderTop: '1px solid var(--border-subtle)', pt: 1 }}>
          <GroupFilterList
            counts={api.stats.groups}
            selected={api.filter.groups}
            onToggle={api.toggleGroup}
            onJump={onJumpToGroup}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid var(--border-subtle)',
          pt: 1,
          mt: 0.5,
        }}
      >
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {api.stats.matching}
          </Box>
          {' / '}
          {api.stats.total}
        </Typography>
        <Button
          onClick={api.clear}
          size="small"
          disabled={!api.isActive}
          sx={{ fontSize: '0.72rem', textTransform: 'none', color: 'text.secondary' }}
        >
          נקה
        </Button>
      </Box>
    </>
  )
}
